import { Megaphone, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { announcementsService, Announcement } from '../services/announcements';

function AnnouncementLink({
  href,
  className,
  children,
}: {
  href: string;
  className: string;
  children: ReactNode;
}) {
  const isExternal = /^https?:\/\//i.test(href);
  if (isExternal) {
    return (
      <a href={href} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  );
}

export default function PromoCarousel() {
  const [promos, setPromos] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);

  const loadPromos = useCallback(async () => {
    try {
      const data = await announcementsService.getAllActive();
      setPromos(data);
      setIndex(0);
    } catch (err) {
      console.error('Failed to load announcements', err);
      setPromos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPromos();
  }, [loadPromos]);

  useEffect(() => {
    if (promos.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % promos.length);
    }, 8000);
    return () => window.clearInterval(timer);
  }, [promos.length]);

  if (loading) {
    return (
      <div className="bg-red-600 py-2 flex items-center justify-center">
        <Loader2 className="w-4 h-4 text-white animate-spin mr-2" />
        <span className="text-xs text-white/80 font-bold uppercase tracking-widest">
          Chargement des annonces...
        </span>
      </div>
    );
  }

  if (promos.length === 0) return null;

  const promo = promos[index];
  const ctaHref = promo.link?.trim() || '/contact';
  const ctaLabel = promo.buttonText?.trim() || 'Réservez maintenant';

  return (
    <div
      className="bg-red-600 text-white py-2.5 px-4 shadow-md z-40 border-b border-red-700/30"
      role="region"
      aria-label="Bande d'annonce"
    >
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-6">
        <div className="flex items-center gap-3 min-w-0 flex-1 text-center sm:text-left">
          <Megaphone className="w-5 h-5 shrink-0 text-white" aria-hidden />
          <p className="text-sm md:text-base font-semibold leading-snug">
            <span className="font-black uppercase tracking-wide mr-2">Bande d&apos;annonce :</span>
            {promo.text}
            {promo.code && (
              <span className="ml-2 inline-flex items-center rounded bg-white/15 px-2 py-0.5 text-xs font-mono font-bold border border-white/25">
                {promo.code}
              </span>
            )}
          </p>
        </div>

        <AnnouncementLink
          href={ctaHref}
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white px-4 py-1.5 text-sm font-bold text-red-600 hover:bg-red-50 transition-colors shadow-sm"
        >
          {ctaLabel}
          <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
        </AnnouncementLink>
      </div>

      {promos.length > 1 && (
        <div className="max-w-7xl mx-auto mt-2 flex justify-center sm:justify-end gap-1.5">
          {promos.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`Annonce ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
